import { useAtom } from "jotai";
import { atomWithStorage } from "jotai/utils";

import { RequestStatusEnum } from "#/services/bridge/bridge.types";
import { useAccount } from "#/store/wallet/account/account";

interface BridgeTransaction {
  requestHash: string;
  status: RequestStatusEnum;
}

interface BridgeState {
  [account: string]: BridgeTransaction[];
}

export const bridgeStateAtom = atomWithStorage<BridgeState>("bridgeState", {});

export const useBridgeState = () => {
  const { account } = useAccount();
  const [bridgeState, setBridgeState] = useAtom(bridgeStateAtom);

  const transactions = account ? (bridgeState[account] ?? []) : [];
  const pendingTransactions = transactions.filter(
    (tx) => tx.status < RequestStatusEnum.FULFILLED,
  );
  const settledTransactions = transactions.filter(
    (tx) => tx.status >= RequestStatusEnum.FULFILLED,
  );

  const addBridgeTx = (requestHash: string) => {
    if (!account) return;

    setBridgeState((prev) => {
      const accountTxs = prev[account] ?? [];

      // Check if transaction already exists
      if (accountTxs.some((tx) => tx.requestHash === requestHash)) {
        return prev;
      }

      return {
        ...prev,
        [account]: [
          ...accountTxs,
          { requestHash, status: RequestStatusEnum.PENDING },
        ],
      };
    });
  };

  const updateBridgeTxStatus = (
    requestHash: string,
    status: RequestStatusEnum,
  ) => {
    if (!account) return;

    setBridgeState((prev) => {
      const accountTxs = prev[account] ?? [];

      return {
        ...prev,
        [account]: accountTxs.map((tx) =>
          tx.requestHash === requestHash ? { ...tx, status } : tx,
        ),
      };
    });
  };

  return {
    transactions,
    pendingTransactions,
    settledTransactions,
    addBridgeTx,
    updateBridgeTxStatus,
    setBridgeState,
  };
};
